// Per-user dashboard widget-preference persistence (ADR-0009 / PRD #185).
//
// Loads the signed-in user's saved widget layout for a dashboard `surface`
// from `GET /api/dashboard-prefs/:surface` on mount, and persists the ordered
// SIZED entry list back via `PUT` on every change (debounced so a rapid
// pin/unpin/reorder burst collapses into one write).
//
// The server stores an ORDERED array of sized entries (`{ id, w?, h? }`) —
// array order is the on-screen order, and `w`/`h` are the widget's footprint in
// grid cells (issue #215). A missing `w`/`h` resolves to the per-kind catalogue
// default at render. An "absent" row (`present: false`) means the user has
// never saved this surface; the caller resolves that to the surface's default
// set. A legacy `string[]` row is coerced to `{ id }` entries server-side, so
// older saved layouts still load.
//
// This is UI preference, not domain state: it reads/writes the plain
// firm-infrastructure endpoint, NOT the ts-rest ontology surface.

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '@/lib/api';
import {
    coerceLegacy,
    type LegacyOrSizedEntry,
    type StoredWidgetEntry,
} from '@/components/my-page/grid/widget-sizes';

/** Opaque stable dashboard keys — must match the server's KNOWN_SURFACES. */
export type DashboardSurface = 'desk_my_page' | 'view_kpi';

export interface PrefsResponse {
    surface: string;
    present: boolean;
    /** Ordered sized entries; the server coerces a legacy `string[]` to `{ id }`. */
    pinnedWidgets: StoredWidgetEntry[];
    updatedAt: string | null;
}

export interface DashboardPrefsState {
    /** Ordered sized entries — the saved layout, or [] until the first load resolves. */
    widgets: StoredWidgetEntry[];
    /** false while the initial GET is in flight. */
    loaded: boolean;
    /** true when the user has a saved row; false → defaults apply. */
    present: boolean;
}

const SAVE_DEBOUNCE_MS = 400;

export async function fetchPrefs(surface: DashboardSurface): Promise<PrefsResponse | null> {
    const res = await apiFetch(`/api/dashboard-prefs/${surface}`);
    if (!res.ok) return null;
    const raw = (await res.json()) as Omit<PrefsResponse, 'pinnedWidgets'> & {
        pinnedWidgets: LegacyOrSizedEntry[];
    };
    // Belt-and-braces: the server already coerces, but a stale cache or an older
    // server could still hand back bare strings — normalise on the way in.
    return { ...raw, pinnedWidgets: (raw.pinnedWidgets ?? []).map(coerceLegacy) };
}

export async function savePrefs(
    surface: DashboardSurface,
    widgets: StoredWidgetEntry[],
): Promise<void> {
    await apiFetch(`/api/dashboard-prefs/${surface}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinnedWidgets: widgets }),
    });
}

/**
 * Load + debounced-save a user's widget layout for one surface.
 *
 * `setWidgets` updates local state immediately (optimistic) and schedules a
 * debounced PUT. The first server load is reflected via `state.loaded`; a
 * caller that needs to apply defaults reads `state.present === false` once
 * `loaded` is true.
 */
export function useDashboardPrefs(surface: DashboardSurface): {
    state: DashboardPrefsState;
    setWidgets: (next: StoredWidgetEntry[]) => void;
} {
    const [state, setState] = useState<DashboardPrefsState>({
        widgets: [],
        loaded: false,
        present: false,
    });

    const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Guards against a debounced save from a previous surface landing after a
    // surface switch, and against the initial load overwriting an in-flight edit.
    const surfaceRef = useRef(surface);
    surfaceRef.current = surface;

    // Initial load — resets state on surface change.
    useEffect(() => {
        let cancelled = false;
        setState({ widgets: [], loaded: false, present: false });
        void fetchPrefs(surface).then((res) => {
            if (cancelled) return;
            setState({
                widgets: res?.pinnedWidgets ?? [],
                loaded: true,
                present: res?.present ?? false,
            });
        });
        return () => {
            cancelled = true;
        };
    }, [surface]);

    // Flush a pending save on unmount so a fast pin-then-navigate isn't lost.
    useEffect(() => {
        return () => {
            if (saveTimer.current) clearTimeout(saveTimer.current);
        };
    }, []);

    const setWidgets = useCallback((next: StoredWidgetEntry[]) => {
        setState((prev) => ({ ...prev, widgets: next, present: true }));
        if (saveTimer.current) clearTimeout(saveTimer.current);
        const target = surfaceRef.current;
        saveTimer.current = setTimeout(() => {
            void savePrefs(target, next);
        }, SAVE_DEBOUNCE_MS);
    }, []);

    return { state, setWidgets };
}
